import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Trash2, Save, X, Package, CheckSquare, Square, Tag, Clock, Lock } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";

function fmtCountdown(iso) {
  if (!iso) return "";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "abgelaufen";
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  if (d > 0) return `endet in ${d} Tag${d === 1 ? "" : "en"} ${h} Std`;
  const m = Math.floor((ms % 3600000) / 60000);
  return `endet in ${h} Std ${m} Min`;
}

export default function PlansManager() {
  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // current draft (new or existing)
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, f] = await Promise.all([
        api.get("/admin/plans"),
        api.get("/admin/feature-keys"),
      ]);
      setPlans(p.data);
      setFeatures(f.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({
    slug: "", name: "", description: "", monthly_price: 0,
    sale_price: "", sale_until: "",
    features: [], limits: { staff_limit: -1 },
    sort_order: plans.length + 1, active: true,
  });

  const startEdit = (p) => setEditing({
    ...p,
    sale_price: p.sale_price ?? "",
    sale_until: p.sale_until ? p.sale_until.slice(0, 16) : "",  // for datetime-local input
    limits: p.limits || { staff_limit: -1 },
  });

  const toggleFeature = (key) => {
    if (!editing) return;
    const cur = new Set(editing.features || []);
    if (cur.has(key)) cur.delete(key); else cur.add(key);
    setEditing({ ...editing, features: [...cur] });
  };

  const save = async () => {
    if (!editing) return;
    setErr("");
    setSaving(true);
    try {
      const payload = {
        slug: editing.slug.trim().toLowerCase(),
        name: editing.name.trim(),
        description: editing.description || "",
        monthly_price: parseFloat(editing.monthly_price) || 0,
        sale_price: editing.sale_price === "" || editing.sale_price === null
          ? null
          : parseFloat(editing.sale_price),
        sale_until: editing.sale_until
          ? new Date(editing.sale_until).toISOString()
          : null,
        features: editing.features || [],
        limits: {
          staff_limit: editing.limits?.staff_limit === "" || editing.limits?.staff_limit === null || editing.limits?.staff_limit === undefined
            ? -1
            : parseInt(editing.limits.staff_limit, 10),
        },
        sort_order: parseInt(editing.sort_order) || 0,
        active: !!editing.active,
      };
      if (editing.id) {
        await api.put(`/admin/plans/${editing.id}`, payload);
      } else {
        await api.post("/admin/plans", payload);
      }
      setEditing(null);
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const [migration, setMigration] = useState(null);  // { plan, in_use, target_id }
  const [migrating, setMigrating] = useState(false);

  const remove = async (p) => {
    if (!window.confirm(`Paket „${p.name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/plans/${p.id}`);
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Structured "plan_in_use" -> open migration dialog
      if (detail && typeof detail === "object" && detail.error === "plan_in_use") {
        const targets = plans.filter((q) => q.active && q.id !== p.id);
        if (targets.length === 0) {
          alert("Keine anderen aktiven Pakete vorhanden — bitte zuerst eines aktivieren.");
          return;
        }
        setMigration({ plan: p, in_use: detail.in_use, target_id: targets[0].id });
      } else {
        alert((typeof detail === "string" ? detail : detail?.message) || "Löschen fehlgeschlagen");
      }
    }
  };

  const confirmMigrateAndDelete = async () => {
    if (!migration?.target_id) return;
    setMigrating(true);
    try {
      await api.delete(`/admin/plans/${migration.plan.id}`, {
        params: { reassign_to: migration.target_id },
      });
      setMigration(null);
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      alert((typeof detail === "string" ? detail : detail?.message) || "Umbuchen / Löschen fehlgeschlagen");
    } finally {
      setMigrating(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto px-6 py-12" data-testid="plans-manager">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <p className="mv-label mb-2">Plattform-Konfiguration</p>
          <h1 className="font-display text-4xl">Pakete</h1>
          <p className="text-sm mt-2" style={{ color: "var(--mv-text-muted)" }}>
            {plans.length} Pakete · {features.length} Features verfügbar
          </p>
        </div>
        <button data-testid="new-plan-btn" onClick={startNew} className="mv-btn">
          <Plus className="w-4 h-4" strokeWidth={1.5} /> Neues Paket
        </button>
      </div>

      {loading ? (
        <p style={{ color: "var(--mv-text-muted)" }}>Lade...</p>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((p) => (
            <div
              key={p.id}
              data-testid={`plan-card-${p.slug}`}
              className="mv-card p-6 cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => startEdit(p)}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Package className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--mv-primary)" }} />
                    <h3 className="font-display text-xl">{p.name}</h3>
                    {!p.active && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ background: "#e7ebee", color: "#5b6770" }}>
                        Inaktiv
                      </span>
                    )}
                  </div>
                  <p className="text-xs font-mono" style={{ color: "var(--mv-text-muted)" }}>{p.slug}</p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); remove(p); }}
                  data-testid={`delete-plan-${p.slug}`}
                  className="p-1.5 rounded hover:bg-[var(--mv-surface-2)]"
                  title="Löschen"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--mv-text-muted)" }} />
                </button>
              </div>
              {p.description && <p className="text-sm mb-3" style={{ color: "var(--mv-text-muted)" }}>{p.description}</p>}
              {p.on_sale ? (
                <div className="mb-3" data-testid={`plan-sale-${p.slug}`}>
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <p className="font-display text-2xl" style={{ color: "var(--mv-primary)" }}>
                      {p.sale_price.toFixed(2)} <span className="text-sm" style={{ color: "var(--mv-text-muted)" }}>€/Monat</span>
                    </p>
                    <p className="text-base line-through" style={{ color: "var(--mv-text-muted)" }}>
                      {p.monthly_price.toFixed(2)} €
                    </p>
                    <span className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ background: "var(--mv-primary)", color: "#fff" }}>
                      AKTION
                    </span>
                  </div>
                  <p className="text-[11px] mt-1 flex items-center gap-1" style={{ color: "var(--mv-primary)" }}>
                    <Clock className="w-3 h-3" strokeWidth={1.8} /> {fmtCountdown(p.sale_until)}
                  </p>
                </div>
              ) : (
                <p className="font-display text-2xl mb-3">{p.monthly_price.toFixed(2)} <span className="text-sm" style={{ color: "var(--mv-text-muted)" }}>€/Monat</span></p>
              )}
              <p className="text-xs mb-2" style={{ color: "var(--mv-text-muted)" }}>{(p.features || []).length} / {features.length} Features aktiv</p>
              <div className="flex flex-wrap gap-1">
                {(p.features || []).slice(0, 6).map((f) => (
                  <span key={f} className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: "var(--mv-surface-2)" }}>{f}</span>
                ))}
                {(p.features || []).length > 6 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ color: "var(--mv-text-muted)" }}>+{(p.features || []).length - 6}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(44,38,33,0.5)" }}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="plan-edit-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="mv-label mb-1">Paket bearbeiten</p>
                <h2 className="font-display text-2xl">{editing.id ? editing.name : "Neues Paket"}</h2>
              </div>
              <button onClick={() => setEditing(null)} className="p-1.5 -m-1 rounded hover:bg-[var(--mv-surface)]">
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>

            {err && (
              <p data-testid="plan-edit-error" className="text-sm px-3 py-2 rounded mb-3 bg-red-50 text-red-700">{err}</p>
            )}

            <div className="grid md:grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-xs mb-1">Name</label>
                <input data-testid="plan-name-input" className="mv-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs mb-1">Slug (intern)</label>
                <input data-testid="plan-slug-input" className="mv-input font-mono" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })} disabled={!!editing.id} />
              </div>
              <div>
                <label className="block text-xs mb-1">Preis (€/Monat)</label>
                <input type="number" step="0.01" min="0" data-testid="plan-price-input" className="mv-input" value={editing.monthly_price} onChange={(e) => setEditing({ ...editing, monthly_price: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs mb-1">Sortierung</label>
                <input type="number" data-testid="plan-sort-input" className="mv-input" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })} />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-xs mb-1">Beschreibung</label>
              <textarea data-testid="plan-desc-input" className="mv-input min-h-[60px]" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
            </div>

            <div className="mb-5 p-3 rounded-lg" style={{ background: "var(--mv-surface)" }}>
              <p className="mv-label mb-3">Limits</p>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs mb-1">Max. Mitarbeitende</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="-1"
                      step="1"
                      data-testid="plan-staff-limit-input"
                      className="mv-input flex-1"
                      value={editing.limits?.staff_limit ?? -1}
                      onChange={(e) => setEditing({
                        ...editing,
                        limits: { ...(editing.limits || {}), staff_limit: e.target.value === "" ? "" : parseInt(e.target.value, 10) },
                      })}
                    />
                    <button
                      type="button"
                      data-testid="plan-staff-limit-unlimited"
                      onClick={() => setEditing({ ...editing, limits: { ...(editing.limits || {}), staff_limit: -1 } })}
                      className="mv-btn-ghost text-xs whitespace-nowrap"
                      title="Unbegrenzt setzen"
                    >
                      ∞ Unbegrenzt
                    </button>
                  </div>
                  <p className="text-[10px] mt-1 opacity-60">
                    -1 oder &quot;Unbegrenzt&quot; = keine Einschränkung. Andernfalls maximale Anzahl aktiver Mitarbeiter:innen pro Salon.
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-5 p-3 rounded-lg" style={{ background: "var(--mv-surface)" }}>
              <p className="mv-label mb-3 flex items-center gap-1.5">
                <Tag className="w-3.5 h-3.5" strokeWidth={1.5} /> Aktionspreis (optional)
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs mb-1">Rabattierter Preis (€/Monat)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    data-testid="plan-sale-price-input"
                    className="mv-input"
                    value={editing.sale_price}
                    onChange={(e) => setEditing({ ...editing, sale_price: e.target.value })}
                    placeholder="leer = kein Rabatt"
                  />
                </div>
                <div>
                  <label className="block text-xs mb-1">Gültig bis</label>
                  <input
                    type="datetime-local"
                    data-testid="plan-sale-until-input"
                    className="mv-input"
                    value={editing.sale_until}
                    onChange={(e) => setEditing({ ...editing, sale_until: e.target.value })}
                  />
                </div>
              </div>
              {(editing.sale_price || editing.sale_until) && (
                <button
                  type="button"
                  data-testid="plan-sale-clear-btn"
                  onClick={() => setEditing({ ...editing, sale_price: "", sale_until: "" })}
                  className="text-xs underline mt-2"
                  style={{ color: "var(--mv-text-muted)" }}
                >
                  Aktion entfernen
                </button>
              )}
              <p className="text-[11px] mt-2" style={{ color: "var(--mv-text-muted)" }}>
                Nach Ablauf wird automatisch wieder der reguläre Preis angezeigt.
              </p>
            </div>

            <label className="flex items-center gap-2 text-sm mb-5">
              <input type="checkbox" data-testid="plan-active-toggle" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />
              Aktiv (Salons können dieses Paket nutzen)
              <span className="ml-auto text-[11px]" style={{ color: "var(--mv-text-muted)" }}>
                Deaktivieren ist blockiert, solange Salons zugewiesen sind.
              </span>
            </label>

            <div className="mb-5">
              <div className="flex items-center justify-between mb-3">
                <p className="mv-label">Enthaltene Features ({(editing.features || []).length} / {features.length})</p>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setEditing({ ...editing, features: features.map((f) => f.key) })} className="text-xs underline" style={{ color: "var(--mv-text-muted)" }}>Alle</button>
                  <span style={{ color: "var(--mv-text-muted)" }}>·</span>
                  <button type="button" onClick={() => setEditing({ ...editing, features: [] })} className="text-xs underline" style={{ color: "var(--mv-text-muted)" }}>Keine</button>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-1.5">
                {features.map((f) => {
                  const checked = (editing.features || []).includes(f.key);
                  return (
                    <button
                      key={f.key}
                      type="button"
                      data-testid={`feature-toggle-${f.key}`}
                      onClick={() => toggleFeature(f.key)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors"
                      style={{ background: checked ? "var(--mv-surface)" : "transparent", border: "1px solid var(--mv-border)" }}
                    >
                      {checked
                        ? <CheckSquare className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} style={{ color: "var(--mv-primary)" }} />
                        : <Square className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} style={{ color: "var(--mv-text-muted)" }} />}
                      <span className="truncate">{f.label}</span>
                      <span className="ml-auto text-[10px] font-mono opacity-50">{f.key}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <button onClick={() => setEditing(null)} className="mv-btn-ghost" disabled={saving}>Abbrechen</button>
              <button data-testid="plan-save-btn" onClick={save} className="mv-btn" disabled={saving || !editing.name || !editing.slug}>
                <Save className="w-4 h-4" strokeWidth={1.5} /> {saving ? "Speichert…" : "Speichern"}
              </button>
            </div>
          </div>
        </div>
      )}

      {migration && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(44,38,33,0.5)" }}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" data-testid="plan-migrate-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="mv-label mb-1">Salons umbuchen</p>
                <h2 className="font-display text-2xl">Paket „{migration.plan.name}&ldquo; löschen</h2>
              </div>
              <button onClick={() => setMigration(null)} className="p-1.5 -m-1 rounded hover:bg-[var(--mv-surface)]" disabled={migrating}>
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>
            <p className="text-sm mb-4" style={{ color: "var(--mv-text-muted)" }}>
              <strong>{migration.in_use}</strong> Salon(s) nutzen dieses Paket. Wähle ein Zielpaket, auf das alle Salons automatisch umgebucht werden, bevor „{migration.plan.name}&ldquo; gelöscht wird.
            </p>
            <div className="mb-5">
              <label className="block text-xs mb-1">Zielpaket</label>
              <select
                data-testid="plan-migrate-target"
                className="mv-input"
                value={migration.target_id}
                onChange={(e) => setMigration({ ...migration, target_id: e.target.value })}
                disabled={migrating}
              >
                {plans.filter((q) => q.active && q.id !== migration.plan.id).map((q) => (
                  <option key={q.id} value={q.id}>{q.name} — {q.monthly_price.toFixed(2)} €/Monat</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setMigration(null)} className="mv-btn-ghost" disabled={migrating}>Abbrechen</button>
              <button
                data-testid="plan-migrate-confirm-btn"
                onClick={confirmMigrateAndDelete}
                className="mv-btn"
                disabled={migrating || !migration.target_id}
              >
                <Trash2 className="w-4 h-4" strokeWidth={1.5} /> {migrating ? "Wird verarbeitet…" : "Umbuchen & löschen"}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </AdminLayout>
  );
}
